import { api, type ApiResponse } from '@/lib/api';
import type {
  CreateGoalRequest,
  GoalQuery,
  GoalResponse,
  UpdateGoalRequest,
} from '@troia-v3/types';

export interface PaginatedGoalResult {
  items: GoalResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const BASE_URL = '/goals';

/**
 * API client for the Goals module.
 *
 * Mirrors backend endpoints:
 * - GET  /api/goals                — list with pagination/filters
 * - GET  /api/goals/:id            — get single goal
 * - POST /api/goals                — create
 * - PUT  /api/goals/:id            — update (partial)
 * - DELETE /api/goals/:id          — delete
 */
export const goalsApi = {
  async list(query?: GoalQuery): Promise<PaginatedGoalResult> {
    const response = await api.get<ApiResponse<PaginatedGoalResult>>(BASE_URL, { params: query });
    return response.data.data!;
  },

  async getById(id: string): Promise<GoalResponse> {
    const response = await api.get<ApiResponse<GoalResponse>>(`${BASE_URL}/${id}`);
    return response.data.data!;
  },

  async create(data: CreateGoalRequest): Promise<GoalResponse> {
    const response = await api.post<ApiResponse<GoalResponse>>(BASE_URL, data);
    return response.data.data!;
  },

  async update(id: string, data: UpdateGoalRequest): Promise<GoalResponse> {
    const response = await api.put<ApiResponse<GoalResponse>>(`${BASE_URL}/${id}`, data);
    return response.data.data!;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`);
  },
};
